
import { Link, useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { useState, useCallback, useMemo } from "react";
import BackButton from "@/components/header/BackButton";
import MobileMenu from "@/components/header/MobileMenu";
import DesktopNavigation from "@/components/header/DesktopNavigation";
import HeaderControls from "@/components/header/HeaderControls";
import SidebarButton from "@/components/header/SidebarButton";
import { useTheme } from "@/hooks/useTheme";

interface HeaderProps {
  viewMode?: "grid" | "table";
  setViewMode?: (mode: "grid" | "table") => void;
  useBackButton?: boolean;
  pageName?: string;
  sidebarExpanded?: boolean;
  toggleSidebar?: () => void;
}

const Header = ({ 
  viewMode, 
  setViewMode, 
  useBackButton = false,
  pageName = "3DP Hub",
  sidebarExpanded = true,
  toggleSidebar
}: HeaderProps) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme } = useTheme();
  
  // Memoize page name calculation
  const currentPageName = useMemo(() => {
    if (pageName !== "3DP Hub") return pageName;
    if (currentPath === "/parts") return "Parts";
    if (currentPath === "/tutorials") return "Guides";
    if (currentPath === "/tools") return "Tools";
    if (currentPath === "/donate") return "Donate";
    if (currentPath === "/roadmap") return "Roadmap";
    if (currentPath === "/tutorials/create") return "Create New Tutorial";
    return pageName;
  }, [currentPath, pageName]);

  // Memoize boolean calculations
  const shouldShowBackButton = useMemo(() => {
    return currentPath.includes("/parts/") || 
           currentPath.includes("/tutorials/") || 
           currentPath === "/donate" || 
           currentPath === "/roadmap" || 
           currentPath === "/tools" ||
           useBackButton;
  }, [currentPath, useBackButton]);

  const shouldShowSidebarButton = useMemo(() => {
    return currentPath === "/parts" || 
           currentPath === "/tutorials/create" ||
           !!toggleSidebar;
  }, [currentPath, toggleSidebar]);

  const borderClass = useMemo(() => {
    return "border-border";
  }, []);

  // Stabilize sidebar toggle callback
  const handleSidebarToggle = useCallback(() => {
    if (toggleSidebar) {
      toggleSidebar();
    }
  }, [toggleSidebar]);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  return (
    <TooltipProvider>
      <header className="sticky top-0 bg-background border-b border-border z-30 w-full">
        <div className="flex items-center justify-between h-16 w-full px-4 md:px-6">
          {/* Left section with sidebar/back button and logo */}
          <div className="flex items-center">
            {/* Sidebar Button or Back Button */}
            <div className="flex items-center mr-4 z-50">
              {shouldShowSidebarButton ? (
                <SidebarButton 
                  expanded={sidebarExpanded} 
                  toggleSidebar={handleSidebarToggle} 
                  borderClass={borderClass} 
                />
              ) : shouldShowBackButton ? (
                <BackButton borderClass={borderClass} />
              ) : null}
            </div>
            
            {/* Logo - only visible on desktop */}
            <Link to="/" className="font-semibold text-lg mr-auto hidden md:block text-foreground">
              3DP Hub
            </Link>
          </div>
          
          {/* Center section */}
          <div className="flex items-center">
            {/* Mobile Navigation Button - only visible on mobile */}
            {isMobile && (
              <MobileMenu 
                currentPageName={currentPageName}
                borderClass={borderClass}
                isOpen={mobileMenuOpen}
                onToggle={toggleMobileMenu}
              >
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={toggleMobileMenu}
                  className={`h-10 w-10 rounded-md border ${borderClass} mr-4 text-foreground hover:text-foreground`}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </MobileMenu>
            )}
            
            {/* Desktop Navigation */}
            <DesktopNavigation />
          </div>
          
          {/* Right Controls */}
          <div className="flex items-center">
            <HeaderControls 
              viewMode={viewMode} 
              setViewMode={setViewMode} 
              borderClass={borderClass}
            />
          </div>
        </div>
      </header>
    </TooltipProvider>
  );
};

export default Header;
export { Header };
